import { useState, useMemo } from 'react';
import { StatusPill } from '@/components/common/StatusPill';
import { CustomMultiSelect } from '@/components/common/CustomMultiSelect';
import { formatReportDate } from '@/features/reports/services/reportsPresentation';

export function AttendanceReportSection({
  attendance = [],
  attendanceUsers = [],
  attendanceFilters = {},
  onAttendanceFiltersChange,
  onResetFilters,
  customStart,
  onCustomStartChange,
  customEnd,
  onCustomEndChange,
  leaves = [],
  intimations = []
}) {
  const [subView, setSubView] = useState('attendance'); // 'attendance' | 'leaves' | 'intimations'
  const [statusFilter, setStatusFilter] = useState('');

  const employeeOptions = attendanceUsers.map((user) => ({
    value: user.id || user,
    label: user.name || user.id || user
  }));

  const filteredAttendance = useMemo(() => {
    if (!statusFilter) return attendance;
    return attendance.filter((item) => {
      const st = String(item.status || '').toLowerCase();
      if (statusFilter === 'Present') return /present|on time/i.test(st);
      if (statusFilter === 'Absent') return /absent/i.test(st);
      if (statusFilter === 'Half Day') return /half day/i.test(st);
      if (statusFilter === 'Leave') return /leave/i.test(st) || item.hasLeave;
      if (statusFilter === 'Weekly Off') return /weekly off|w/i.test(st);
      if (statusFilter === 'Late') return /late/i.test(st);
      return true;
    });
  }, [attendance, statusFilter]);

  const handleReset = () => {
    setStatusFilter('');
    onResetFilters();
  };

  return (
    <>
      <article className="migration-panel migration-panel--full" style={{ position: 'relative', zIndex: 10 }}>
        <div className="approval-filter-grid" style={{ alignItems: 'flex-end', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          <label className="dashboard-control" style={{ minWidth: '220px', flex: 1 }}>
            <span>Employees</span>
            <CustomMultiSelect
              value={attendanceFilters.employees}
              onChange={(values) => onAttendanceFiltersChange((current) => ({ ...current, employees: values }))}
              options={employeeOptions}
              defaultLabel="All Employees"
            />
          </label>

          <label className="dashboard-control" style={{ minWidth: '160px' }}>
            <span>Status</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="dashboard-select"
              style={{
                height: '42px',
                borderRadius: '10px',
                border: '1px solid #dce4ef',
                padding: '0 12px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#334155',
                background: '#fff',
                width: '100%'
              }}
            >
              <option value="">All Statuses</option>
              <option value="Present">Present / On Time</option>
              <option value="Absent">Absent</option>
              <option value="Half Day">Half Day</option>
              <option value="Leave">Leave</option>
              <option value="Late">Late</option>
              <option value="Weekly Off">Weekly Off</option>
            </select>
          </label>

          <label className="dashboard-control" style={{ minWidth: '150px' }}>
            <span>Starting Date</span>
            <input type="date" value={customStart} onChange={(event) => onCustomStartChange(event.target.value)} />
          </label>
          
          <label className="dashboard-control" style={{ minWidth: '150px' }}>
            <span>Ending Date</span>
            <input type="date" value={customEnd} onChange={(event) => onCustomEndChange(event.target.value)} />
          </label>

          <div style={{ paddingBottom: '4px' }}>
            <button
              type="button"
              className="attendance-cta attendance-cta--gray"
              onClick={handleReset}
              style={{ height: '38px', padding: '0 24px', margin: 0 }}
            >
              Reset
            </button>
          </div>
        </div>
      </article>

      <article className="migration-panel migration-panel--full">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid rgba(226, 232, 240, 0.6)', marginBottom: '16px' }}>
          <div className="reports-sub-tabs" style={{ margin: 0, padding: 0, border: 'none', width: 'auto', display: 'flex', gap: '24px' }}>
            <button
              type="button"
              className={`reports-sub-tab-btn ${subView === 'attendance' ? 'reports-sub-tab-btn--active' : ''}`}
              onClick={() => setSubView('attendance')}
            >
              Attendance Records ({filteredAttendance.length})
            </button>
            <button
              type="button"
              className={`reports-sub-tab-btn ${subView === 'leaves' ? 'reports-sub-tab-btn--active' : ''}`}
              onClick={() => setSubView('leaves')}
            >
              Leaves ({leaves.length})
            </button>
            <button
              type="button"
              className={`reports-sub-tab-btn ${subView === 'intimations' ? 'reports-sub-tab-btn--active' : ''}`}
              onClick={() => setSubView('intimations')}
            >
              Intimations ({intimations.length})
            </button>
          </div>

          <StatusPill tone="info">
            {subView === 'attendance'
              ? `${filteredAttendance.length} records`
              : subView === 'leaves'
              ? `${leaves.length} records`
              : `${intimations.length} records`}
          </StatusPill>
        </div>

        {subView === 'attendance' && (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table reports-premium-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee</th>
                  <th>Punch In</th>
                  <th>Punch Out</th>
                  <th>Status</th>
                  <th>Departure Status</th>
                  <th>Duration</th>
                  <th>Admin Remarks</th>
                </tr>
              </thead>
              <tbody>
                {filteredAttendance.length ? (
                  filteredAttendance.map((item) => {
                    const statusTone =
                      item.status === 'On Time' || item.status === 'Present' || item.status === 'Approved'
                        ? 'success'
                        : item.status === 'Half Day' || item.status === 'Late'
                        ? 'warning'
                        : item.status === 'Early'
                        ? 'info'
                        : item.status === 'Leave'
                        ? 'info'
                        : item.status === 'Absent'
                        ? 'danger'
                        : 'neutral';

                    const outTone =
                      item.outStatus === 'On Time Departure'
                        ? 'success'
                        : item.outStatus === 'Late Departure'
                        ? 'warning'
                        : item.outStatus === 'Early Departure'
                        ? 'danger'
                        : 'neutral';

                    return (
                      <tr key={item.id}>
                        <td data-label="Date" style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {formatReportDate(item.date) || item.dateLabel || item.date}
                        </td>
                        <td data-label="Employee">
                          <div>
                            <div style={{ fontWeight: 600 }}>{item.employeeName || item.employeeId || '-'}</div>
                            {item.role ? <div style={{ fontSize: '11px', color: '#64748b' }}>{item.role}</div> : null}
                          </div>
                        </td>
                        <td data-label="Punch In">
                          {item.punchInTime && item.punchInTime !== '-' ? (
                            <div className="attendance-time-pill attendance-time-pill--in">
                              {item.punchInTime}
                            </div>
                          ) : (
                            <span className="approval-table__empty">-</span>
                          )}
                        </td>
                        <td data-label="Punch Out">
                          {item.punchOutTime && item.punchOutTime !== '-' ? (
                            <div className="attendance-time-pill attendance-time-pill--out">
                              {item.punchOutTime}
                            </div>
                          ) : (
                            <span className="approval-table__empty">-</span>
                          )}
                        </td>
                        <td data-label="Status">
                          <StatusPill tone={statusTone}>
                            {item.status || '-'}
                          </StatusPill>
                        </td>
                        <td data-label="Departure Status">
                          {item.outStatus && item.outStatus !== '-' ? (
                            <StatusPill tone={outTone}>
                              {item.outStatus}
                            </StatusPill>
                          ) : (
                            <span className="approval-table__empty">-</span>
                          )}
                        </td>
                        <td data-label="Duration" style={{ whiteSpace: 'nowrap' }}>
                          {item.duration || '-'}
                        </td>
                        <td data-label="Admin Remarks" className="approval-table__copy" style={{ maxWidth: '240px' }}>
                          {item.adminRemarks || '-'}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="dashboard-table__empty">No attendance report data found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {subView === 'leaves' && (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table reports-premium-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Leave Type</th>
                  <th>Day Type</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Admin Remarks</th>
                </tr>
              </thead>
              <tbody>
                {leaves.length ? (
                  leaves.map((leave, idx) => {
                    const leaveTone = /approved/i.test(leave.status)
                      ? 'success'
                      : /reject/i.test(leave.status)
                      ? 'danger'
                      : 'warning';

                    return (
                      <tr key={leave.id || `leave-${idx}`}>
                        <td data-label="Employee">
                          <div>
                            <div style={{ fontWeight: 600 }}>{leave.employeeName || leave.employeeId || '-'}</div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>{leave.employeeId}</div>
                          </div>
                        </td>
                        <td data-label="Leave Type">
                          <StatusPill tone="info">{leave.leaveType || 'Casual Leave'}</StatusPill>
                        </td>
                        <td data-label="Day Type">{leave.dayType || 'Full Day'}</td>
                        <td data-label="Start Date" style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {formatReportDate(leave.startDate) || leave.startDate || '-'}
                        </td>
                        <td data-label="End Date" style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {formatReportDate(leave.endDate) || leave.endDate || '-'}
                        </td>
                        <td data-label="Reason" className="approval-table__copy" style={{ maxWidth: '280px' }}>
                          {leave.reason || '-'}
                        </td>
                        <td data-label="Status">
                          <StatusPill tone={leaveTone}>{leave.status || 'Pending'}</StatusPill>
                        </td>
                        <td data-label="Admin Remarks" className="approval-table__copy">
                          {leave.adminRemarks || '-'}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="dashboard-table__empty">No leave records found for this period.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {subView === 'intimations' && (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table reports-premium-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee</th>
                  <th>Intimation Type</th>
                  <th>Miss Punch Type</th>
                  <th>Punch Time</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Admin Remarks</th>
                </tr>
              </thead>
              <tbody>
                {intimations.length ? (
                  intimations.map((int, idx) => {
                    const intTone = /approved/i.test(int.status)
                      ? 'success'
                      : /reject/i.test(int.status)
                      ? 'danger'
                      : 'warning';

                    return (
                      <tr key={int.id || `int-${idx}`}>
                        <td data-label="Date" style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {formatReportDate(int.date) || int.date || '-'}
                        </td>
                        <td data-label="Employee">
                          <div>
                            <div style={{ fontWeight: 600 }}>{int.employeeName || int.employeeId || '-'}</div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>{int.employeeId}</div>
                          </div>
                        </td>
                        <td data-label="Intimation Type">
                          <StatusPill tone="warning">{int.type || '-'}</StatusPill>
                        </td>
                        <td data-label="Miss Punch Type">{int.missPunchType || '-'}</td>
                        <td data-label="Punch Time">
                          {int.punchTime ? (
                            <div className="attendance-time-pill attendance-time-pill--in">
                              {int.punchTime}
                            </div>
                          ) : (
                            <span className="approval-table__empty">-</span>
                          )}
                        </td>
                        <td data-label="Reason" className="approval-table__copy" style={{ maxWidth: '280px' }}>
                          {int.reason || '-'}
                        </td>
                        <td data-label="Status">
                          <StatusPill tone={intTone}>{int.status || 'Submitted'}</StatusPill>
                        </td>
                        <td data-label="Admin Remarks" className="approval-table__copy">
                          {int.adminRemarks || '-'}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="dashboard-table__empty">No intimation records found for this period.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </article>
    </>
  );
}
